// L3 — declarative interaction (INTERACTION-MODEL-V1.md §2–§8).
// TRIGGER → typed CONDITIONS (AST) → ACTIONS → EVENTS. Everything is
// checked statically; nothing is evaluated. Unsupported triggers/actions are
// rejected by the structural layer (closed unions).

import { ENGINE_LIMITS } from "../limits.js";
import {
  ARITHMETIC_OPS,
  COMPARISON_OPS,
  GESTURE_TRIGGERS,
  type Action,
  type Expr,
  type Rule,
  type Trigger,
} from "../schema/document.js";
import type { IssueSink } from "../issues.js";
import { BEHAVIOR_REGISTRY_V1, resolveComponent } from "../registry.js";
import type { DocIndex } from "./context.js";

type ExprType = "number" | "boolean" | "string" | "invalid";

interface ExprContext {
  readonly index: DocIndex;
  readonly sink: IssueSink;
  readonly allowRandom: boolean;
}

function measure(e: Expr, depth = 1): { nodes: number; depth: number } {
  if (typeof e !== "object") return { nodes: 1, depth };
  const children: Expr[] = "args" in e ? [...e.args] : "arg" in e ? [e.arg] : [];
  return children.reduce(
    (acc, c) => {
      const m = measure(c, depth + 1);
      return { nodes: acc.nodes + m.nodes, depth: Math.max(acc.depth, m.depth) };
    },
    { nodes: 1, depth }
  );
}

/** Static type of an expression; reports unknown references and type mismatches. */
export function typeOfExpr(e: Expr, path: string, ctx: ExprContext): ExprType {
  const { index, sink } = ctx;
  if (typeof e === "number") return "number";
  if (typeof e === "boolean") return "boolean";
  if (typeof e === "string") return "string";
  if ("var" in e) {
    const v = index.variables.get(e.var)?.value;
    if (!v) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown variable "${e.var}".`, { kind: "variable", id: e.var });
      return "invalid";
    }
    return v.type === "enum" ? "string" : v.type;
  }
  if ("elementState" in e) {
    if (!index.elements.has(e.elementState)) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown element "${e.elementState}".`, {
        kind: "element",
        id: e.elementState,
      });
      return "invalid";
    }
    return "string";
  }
  if ("scene" in e) return "string";
  if ("field" in e) {
    if (!index.fields.get(e.form)?.has(e.field)) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown field "${e.form}.${e.field}".`, { kind: "field", id: e.field });
      return "invalid";
    }
    return "boolean";
  }
  if ("answered" in e) {
    const q = index.questions.get(e.answered)?.value;
    if (!q) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown question "${e.answered}".`, { kind: "question", id: e.answered });
      return "invalid";
    }
    if (e.option !== undefined && (q.kind === "scale" || !q.options.some((o) => o.id === e.option))) {
      sink.add("UNKNOWN_REFERENCE", `${path}.option`, `Unknown option "${e.option}".`, {
        kind: "option",
        id: e.option,
      });
      return "invalid";
    }
    return "boolean";
  }
  if ("timer" in e) {
    if (!index.timers.has(e.timer)) {
      sink.add("UNKNOWN_REFERENCE", path, `Unknown timer "${e.timer}".`, { kind: "timer", id: e.timer });
      return "invalid";
    }
    return e.prop === "remainingMs" ? "number" : "boolean";
  }
  if ("random" in e) {
    if (!ctx.allowRandom) {
      sink.add(
        "RANDOM_NOT_ALLOWED_IN_CONDITION",
        path,
        "Random values are only allowed in action values, not in conditions.",
        {}
      );
      return "invalid";
    }
    if (e.random.min > e.random.max) sink.add("INVALID_VALUE", `${path}.random`, "Random minimum exceeds maximum.", {});
    return "number";
  }
  const expect = (arg: Expr, i: number, want: ExprType, p = `${path}.args[${String(i)}]`): ExprType => {
    const t = typeOfExpr(arg, p, ctx);
    if (t !== "invalid" && t !== want) {
      sink.add("EXPRESSION_TYPE_MISMATCH", p, `Expected a ${want} expression, found ${t}.`, {
        expected: want,
        actual: t,
      });
      return "invalid";
    }
    return t;
  };
  if ("arg" in e) {
    const want: ExprType = e.op === "not" ? "boolean" : "number";
    return expect(e.arg, 0, want, `${path}.arg`) === "invalid" ? "invalid" : want;
  }
  const op = e.op;
  if (op === "and" || op === "or") {
    const ok = e.args.map((a, i) => expect(a, i, "boolean")).every((t) => t !== "invalid");
    return ok ? "boolean" : "invalid";
  }
  if (op === "clamp" || (ARITHMETIC_OPS as readonly string[]).includes(op)) {
    const ok = e.args.map((a, i) => expect(a, i, "number")).every((t) => t !== "invalid");
    return ok ? "number" : "invalid";
  }
  if ((COMPARISON_OPS as readonly string[]).includes(op)) {
    const [a, b] = e.args as readonly [Expr, Expr];
    const ta = typeOfExpr(a, `${path}.args[0]`, ctx);
    const tb = typeOfExpr(b, `${path}.args[1]`, ctx);
    if (ta === "invalid" || tb === "invalid") return "invalid";
    const ordering = op !== "==" && op !== "!=";
    if (ta !== tb || (ordering && ta !== "number")) {
      sink.add("EXPRESSION_TYPE_MISMATCH", path, `Operator ${op} cannot compare ${ta} with ${tb}.`, {
        op,
        left: ta,
        right: tb,
      });
      return "invalid";
    }
    return "boolean";
  }
  return "invalid";
}

function checkExpr(e: Expr, path: string, ctx: ExprContext, want?: ExprType): ExprType {
  const m = measure(e);
  if (m.nodes > ENGINE_LIMITS.exprNodes || m.depth > ENGINE_LIMITS.exprDepth) {
    ctx.sink.add(
      "EXPRESSION_TOO_COMPLEX",
      path,
      `Expressions are limited to ${ENGINE_LIMITS.exprNodes} nodes and depth ${ENGINE_LIMITS.exprDepth}.`,
      {
        nodes: m.nodes,
        depth: m.depth,
      }
    );
    return "invalid";
  }
  const t = typeOfExpr(e, path, ctx);
  if (want && t !== "invalid" && t !== want) {
    ctx.sink.add("EXPRESSION_TYPE_MISMATCH", path, `Expected a ${want} expression, found ${t}.`, {
      expected: want,
      actual: t,
    });
    return "invalid";
  }
  return t;
}

const isMedia = (index: DocIndex, id: string) => {
  const t = index.elements.get(id)?.value.type;
  return t === "video" || t === "audio";
};
const hasBehavior = (index: DocIndex, id: string, type: keyof typeof BEHAVIOR_REGISTRY_V1) =>
  index.elements.get(id)?.value.behaviors?.some((b) => b.type === type) ?? false;

function checkTrigger(t: Trigger, path: string, index: DocIndex, sink: IssueSink): void {
  const bad = (field: string, message: string, id: string) => {
    sink.add("TRIGGER_TARGET_INVALID", `${path}.${field}`, message, { id });
  };
  const element = (id: string, field = "target") => {
    if (!index.elements.has(id)) bad(field, `Unknown element "${id}".`, id);
    return index.elements.has(id);
  };
  switch (t.type) {
    case "pointer.tap":
    case "pointer.hover.enter":
    case "pointer.hover.leave":
      element(t.target);
      return;
    case "pointer.drag.start":
    case "pointer.drag.end":
    case "drag.drop":
      if (element(t.target) && !hasBehavior(index, t.target, "draggable"))
        bad("target", `Drag triggers need a draggable behavior on "${t.target}".`, t.target);
      if (t.type === "drag.drop" && t.onto !== undefined) {
        if (element(t.onto, "onto")) {
          const targets = index.elements.get(t.target)?.value.behaviors?.find((b) => b.type === "draggable");
          if (targets?.type === "draggable" && targets.dropTargets && !targets.dropTargets.includes(t.onto))
            bad("onto", `"${t.onto}" is not a declared drop target of "${t.target}".`, t.onto);
        }
      }
      return;
    case "scene.enter":
    case "scene.exit":
    case "scene.timelineEnd":
    case "timeline.at": {
      const scene = index.scenes.get(t.scene)?.value;
      if (!scene) {
        bad("scene", `Unknown scene "${t.scene}".`, t.scene);
        return;
      }
      if ((t.type === "scene.timelineEnd" || t.type === "timeline.at") && scene.duration === null)
        bad("scene", `Scene "${t.scene}" has no timeline (duration null).`, t.scene);
      if (t.type === "timeline.at" && scene.duration !== null && t.atMs > scene.duration)
        bad("atMs", "Instant is beyond the scene duration.", t.scene);
      return;
    }
    case "timer.elapsed":
    case "timer.tick":
      if (!index.timers.has(t.timer)) bad("timer", `Unknown timer "${t.timer}".`, t.timer);
      return;
    case "variable.changed":
      if (!index.variables.has(t.var)) bad("var", `Unknown variable "${t.var}".`, t.var);
      return;
    case "media.started":
    case "media.paused":
    case "media.ended":
    case "media.blocked":
    case "media.progress":
      if (!isMedia(index, t.target)) bad("target", `"${t.target}" is not a video or audio element.`, t.target);
      return;
    case "component.event": {
      const el = index.elements.get(t.target)?.value;
      if (el?.type !== "component") {
        bad("target", `"${t.target}" is not a component.`, t.target);
        return;
      }
      const r = resolveComponent(el.component);
      if (r.ok && !r.definition.triggers.includes(t.event))
        bad("event", `Component ${el.component} does not expose "${t.event}".`, t.target);
      return;
    }
    case "survey.answered":
    case "survey.completed":
    case "survey.exited":
      if (index.doc.survey?.id !== t.survey) bad("survey", `Unknown survey "${t.survey}".`, t.survey);
      else if (t.type === "survey.answered" && t.question !== undefined && !index.questions.has(t.question))
        bad("question", `Unknown question "${t.question}".`, t.question);
      return;
    case "form.submitted":
    case "form.invalid":
      if (!index.forms.has(t.form)) bad("form", `Unknown form "${t.form}".`, t.form);
      return;
    case "game.event":
      if (!index.doc.game) bad("event", "game.event needs a declared game (game module).", t.event);
      // A segment signal names a runner segment; every other signal names an element.
      if (t.target !== undefined && t.event === "segment") {
        if (!(index.doc.game?.runner?.segments ?? []).some((s) => s.id === t.target))
          bad("target", `Unknown runner segment "${t.target}".`, t.target);
      } else if (t.target !== undefined) element(t.target);
      return;
    case "collision.enter":
    case "collision.exit":
    case "creative.ready":
    case "creative.visibility":
      return; // collisions: game layer
  }
}

interface ActionContext {
  readonly index: DocIndex;
  readonly sink: IssueSink;
  readonly rule: Rule;
  readonly gesture: boolean;
  readonly afterDepth: number;
}

function checkActions(actions: readonly Action[], path: string, ctx: ActionContext): void {
  actions.forEach((a, i) => {
    checkAction(a, `${path}[${String(i)}]`, ctx);
  });
}

function checkAction(a: Action, path: string, ctx: ActionContext): void {
  const { index, sink } = ctx;
  const exprCtx: ExprContext = { index, sink, allowRandom: true };
  const bad = (field: string, message: string, id: string) => {
    sink.add("ACTION_TARGET_INVALID", `${path}.${field}`, message, { id });
  };
  const element = (id: string) => {
    if (!index.elements.has(id)) bad("target", `Unknown element "${id}".`, id);
    return index.elements.get(id)?.value;
  };
  switch (a.type) {
    case "scene.goTo":
      if (!index.scenes.has(a.scene)) bad("scene", `Unknown scene "${a.scene}".`, a.scene);
      return;
    case "scene.next":
    case "scene.prev":
    case "game.start":
      return;
    case "game.end":
      if (a.outcome === "auto" && !index.doc.game)
        bad("outcome", "outcome auto resolves the declared game's win condition; there is no game module.", "auto");
      return;
    case "element.relocate": {
      const el = element(a.target);
      if (!index.elements.has(a.area)) bad("area", `Unknown element "${a.area}".`, a.area);
      else if (a.area === a.target) bad("area", "An element cannot be its own relocation area.", a.area);
      else if (el && (index.parents.get(a.target)?.kind !== "scene" || index.parents.get(a.area)?.kind !== "scene"))
        bad("target", "Relocation moves top-level layers inside a top-level area.", a.target);
      return;
    }
    case "link.open":
      if (!index.links.has(a.link)) bad("link", `Unknown link "${a.link}".`, a.link);
      if (!ctx.gesture)
        sink.add(
          "ACTION_REQUIRES_GESTURE",
          path,
          "link.open runs only directly from a user gesture (pointer.tap, drag.drop, form.submitted).",
          {
            trigger: ctx.rule.on.type,
          }
        );
      return;
    case "media.unmute":
      if (!ctx.gesture)
        sink.add("ACTION_REQUIRES_GESTURE", path, "Unmuting runs only directly from a user gesture.", {
          trigger: ctx.rule.on.type,
        });
      if (!isMedia(index, a.target)) bad("target", `"${a.target}" is not a video or audio element.`, a.target);
      return;
    case "media.play":
    case "media.pause":
    case "media.toggle":
    case "media.mute":
    case "media.restart":
      if (!isMedia(index, a.target)) bad("target", `"${a.target}" is not a video or audio element.`, a.target);
      return;
    case "element.show":
    case "element.hide":
    case "element.toggle":
      element(a.target);
      return;
    case "element.setState": {
      const el = element(a.target);
      if (el && !Object.hasOwn(el.states ?? {}, a.state))
        bad("state", `Element "${a.target}" has no state "${a.state}".`, a.state);
      return;
    }
    case "animation.play":
    case "animation.stop": {
      const el = element(a.target);
      // Tracks are per format (base list or a variant's override list, ADR-0020): the id must
      // exist in at least one of them; the runtime plays it where the active variant has it.
      const tracks = [
        ...(el?.animations ?? []),
        ...index.doc.layouts.flatMap((l) => l.overrides?.[a.target]?.animations ?? []),
      ].filter((t) => t.id === a.track);
      if (el && tracks.length === 0) bad("track", `Element "${a.target}" has no track "${a.track}".`, a.track);
      else if (a.type === "animation.play" && tracks.some((t) => t.start.mode !== "manual"))
        bad("track", `Track "${a.track}" is timeline-driven; only manual tracks are played by actions.`, a.track);
      return;
    }
    case "var.set":
    case "var.increment":
    case "var.reset":
    case "random.choose": {
      const v = index.variables.get(a.var)?.value;
      if (!v) {
        bad("var", `Unknown variable "${a.var}".`, a.var);
        return;
      }
      if (a.type === "var.set") {
        const want = v.type === "enum" ? "string" : v.type;
        const t = checkExpr(a.value, `${path}.value`, exprCtx, want);
        if (t === "string" && v.type === "enum" && typeof a.value === "string" && !v.values.includes(a.value))
          sink.add("INVALID_VALUE", `${path}.value`, `"${a.value}" is not a value of enum "${v.id}".`, {
            allowed: v.values.join("|"),
          });
      }
      if (a.type === "var.increment") {
        if (v.type !== "number") bad("var", `Variable "${a.var}" is not a number.`, a.var);
        checkExpr(a.by, `${path}.by`, exprCtx, "number");
      }
      if (a.type === "random.choose") {
        if (v.type !== "enum") {
          bad("var", `random.choose needs an enum variable.`, a.var);
          return;
        }
        const from = a.from ?? v.values;
        if (a.from?.some((x) => !v.values.includes(x))) bad("from", `Values must belong to enum "${v.id}".`, a.var);
        if (a.weights && a.weights.length !== from.length) bad("weights", "One weight per candidate value.", a.var);
      }
      return;
    }
    case "timer.start":
    case "timer.stop":
    case "timer.reset":
      if (!index.timers.has(a.timer)) bad("timer", `Unknown timer "${a.timer}".`, a.timer);
      return;
    case "component.command": {
      const el = element(a.target);
      if (el && el.type !== "component") {
        bad("target", `"${a.target}" is not a component.`, a.target);
        return;
      }
      if (el?.type === "component") {
        const r = resolveComponent(el.component);
        if (r.ok && !r.definition.commands.includes(a.command))
          bad("command", `Component ${el.component} does not accept "${a.command}".`, a.target);
      }
      return;
    }
    case "survey.start":
      if (index.doc.survey?.id !== a.survey) bad("survey", `Unknown survey "${a.survey}".`, a.survey);
      return;
    case "form.submit":
    case "form.reset":
      if (!index.forms.has(a.form)) bad("form", `Unknown form "${a.form}".`, a.form);
      return;
    case "spawn":
      checkExpr(a.at.x, `${path}.at.x`, exprCtx, "number");
      checkExpr(a.at.y, `${path}.at.y`, exprCtx, "number");
      return; // template checks: game layer
    case "despawn":
      if (a.target.other && ctx.rule.on.type !== "collision.enter" && ctx.rule.on.type !== "collision.exit")
        bad("target", "despawn {other} is only meaningful in collision rules.", ctx.rule.id);
      if (a.target.id !== undefined) element(a.target.id);
      return;
    case "game.restart":
      if (!index.scenes.has(a.scene)) bad("scene", `Unknown scene "${a.scene}".`, a.scene);
      return;
    case "event.emit": {
      const decl = index.events.get(a.name)?.value;
      if (!decl) {
        sink.add("UNDECLARED_EVENT", `${path}.name`, `Custom event "${a.name}" is not declared in events.`, {
          name: a.name,
        });
        return;
      }
      for (const [prop, value] of Object.entries(a.props ?? {})) {
        const spec = decl.props?.[prop];
        const pp = `${path}.props.${prop}`;
        if (!spec) {
          sink.add("EVENT_PROPS_INVALID", pp, `Event "${a.name}" declares no prop "${prop}".`, { prop });
          continue;
        }
        if (spec.type === "int") checkExpr(value, pp, exprCtx, "number");
        else if (typeof value !== "string" || !spec.values.includes(value))
          sink.add("EVENT_PROPS_INVALID", pp, `Prop "${prop}" takes one of its declared literal values.`, {
            allowed: spec.values.join("|"),
          });
      }
      return;
    }
    case "rule.enable":
    case "rule.disable":
      if (!index.rules.has(a.rule)) bad("rule", `Unknown rule "${a.rule}".`, a.rule);
      return;
    case "after":
      if (ctx.afterDepth + 1 > ENGINE_LIMITS.afterNesting)
        sink.add("NESTING_TOO_DEEP", path, `"after" may be nested at most ${ENGINE_LIMITS.afterNesting} levels.`, {
          max: ENGINE_LIMITS.afterNesting,
        });
      // Deferred actions lose user activation.
      checkActions(a.do, `${path}.do`, { ...ctx, gesture: false, afterDepth: ctx.afterDepth + 1 });
      return;
  }
}

/** Variables written by a list of actions (including deferred ones). */
function writes(actions: readonly Action[]): Set<string> {
  const out = new Set<string>();
  for (const a of actions) {
    if (a.type === "var.set" || a.type === "var.increment" || a.type === "var.reset" || a.type === "random.choose")
      out.add(a.var);
    if (a.type === "after") for (const v of writes(a.do)) out.add(v);
  }
  return out;
}

const goesTo = (actions: readonly Action[], scene: string): boolean =>
  actions.some((a) => (a.type === "scene.goTo" && a.scene === scene) || (a.type === "after" && goesTo(a.do, scene)));

/**
 * Self-triggering patterns. An UNCONDITIONAL rule that re-triggers itself
 * (variable.changed(x) → writes x; scene.enter(s) → goTo s) always runs into
 * the runtime cascade limit: rejected. Other cycles between rules may be
 * guarded by conditions: reported as warnings with the chain.
 */
function checkCycles(index: DocIndex, sink: IssueSink): void {
  const rules = index.doc.rules ?? [];
  const edges = new Map<string, Set<string>>(); // var → vars written by rules it triggers
  rules.forEach((r, i) => {
    const p = `$.rules[${String(i)}]`;
    const unguarded = r.if === undefined && r.once !== true && (r.cooldownMs ?? 0) === 0;
    if (r.on.type === "variable.changed") {
      const w = writes(r.do);
      if (unguarded && w.has(r.on.var)) {
        sink.add(
          "INTERACTION_CYCLE",
          p,
          `Rule "${r.id}" unconditionally re-triggers itself through variable "${r.on.var}".`,
          { rule: r.id }
        );
        return;
      }
      const set = edges.get(r.on.var) ?? new Set<string>();
      for (const v of w) set.add(v);
      edges.set(r.on.var, set);
    }
    if (r.on.type === "scene.enter" && unguarded && goesTo(r.do, r.on.scene))
      sink.add("INTERACTION_CYCLE", p, `Rule "${r.id}" unconditionally re-enters scene "${r.on.scene}".`, {
        rule: r.id,
      });
  });
  // Cycles between variables through several (guarded) rules: warning.
  const state = new Map<string, "visiting" | "done">();
  const reported = new Set<string>();
  const visit = (v: string, stack: string[]) => {
    state.set(v, "visiting");
    for (const next of edges.get(v) ?? []) {
      if (state.get(next) === "visiting") {
        const chain = [...stack.slice(stack.indexOf(next)), next].join(" → ");
        if (!reported.has(chain)) {
          reported.add(chain);
          sink.warn(
            "POSSIBLE_INTERACTION_CYCLE",
            "$.rules",
            `Rules may re-trigger each other through variables ${chain}; the runtime stops cascades at depth ${ENGINE_LIMITS.cascadeDepth}.`,
            {
              chain,
            }
          );
        }
      } else if (!state.has(next)) visit(next, [...stack, next]);
    }
    state.set(v, "done");
  };
  for (const v of [...edges.keys()].sort()) if (!state.has(v)) visit(v, [v]);
}

export function validateInteraction(index: DocIndex, sink: IssueSink): void {
  (index.doc.rules ?? []).forEach((rule, i) => {
    const p = `$.rules[${String(i)}]`;
    checkTrigger(rule.on, `${p}.on`, index, sink);
    if (rule.if !== undefined) checkExpr(rule.if, `${p}.if`, { index, sink, allowRandom: false }, "boolean");
    checkActions(rule.do, `${p}.do`, { index, sink, rule, gesture: GESTURE_TRIGGERS.has(rule.on.type), afterDepth: 0 });
  });
  checkCycles(index, sink);
}
